import type {
  AllFulfillmentTypesType,
  OrderFulfillmentType,
  OrderGetResponseType,
  PaymentPreferenceType,
} from "@sellar-io/api-v2/types";
import {
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  getCourierDeliveryAndDispatchDates,
  getPaymentDue,
  canEditInvoice,
  validateUpsWeight,
} from "@sellar-io/common";
import { UPS_WEIGHT_CHANGE_DATE } from "../../../constants/sellarConstants.shared";
import dayjs from "dayjs";
import { ReactElement, useEffect, useMemo } from "react";
import {
  Controller,
  UseFormReturn,
  useFormState,
  useWatch,
} from "react-hook-form";
import { Alert } from "../../../components/alert/Alert";
import {
  useCurrentDate,
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { Switch, InputDate, InputNote } from "@sellar-io/components";
import {
  ORDER_FULFILLMENT_TYPES,
  ORDER_PAYMENT_TYPES,
} from "../../../constants/sellarConstants.shared";
import { useCourierDeliveryScheduleList } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { useSupplier } from "../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { renderDeliveryDateTitle } from "../../../utils/sellarHelpers";
import { getNextDirectDeliveryDate } from "../helpers/DeliveryDateHelpers/getNextDeliveryDate";
import { OrderConfirmationFormType } from "./ConfirmOrderForm";
import { getConfirmationFulfillmentOptionValue } from "./getConfirmationFulfillmentOptionValue";
import { OrderPaymentMethodFormFragment } from "./OrderPaymentMethodFormFragment";
import { twMerge } from "tailwind-merge";
import { useDeliveryOptions } from "../../../hooks/useDeliveryOptions";
import { Icon, InputField } from "@sellar-io/components";
import { useIsSellarAdmin } from "../../../hooks/useIsSellarAdmin";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import { SellarDistributionFulfillmentChangeFormFragment } from "./SellarDistributionFulfillmentChangeFormFragment/SellarDistributionFulfillmentChangeFormFragment";

/**
 * TODO: This component needs refactoring into more generic fragment
 *       This is because the ConfirmOrderModalFormFragment can be used in both confirm order modal and edit order details modal
 */

export function ConfirmOrderModalFormFragment({
  modifyModal, // indicates whether fragment is used in confirm or edit order details modal
  order,
  form,
}: {
  modifyModal: boolean;
  order: OrderGetResponseType;
  form: UseFormReturn<OrderConfirmationFormType>;
}): ReactElement {
  const currentDate = useCurrentDate();
  const isAdmin = useIsSellarAdmin();

  const {
    paidAt,
    paymentType,
    dispatchedAt,
    deliveredAt,
    fulfillmentType,
    dispatchPlannedAt,
    deliveryPlannedAt,
    hasNoDraughtDutyRelief,
    fulfillmentChangeReason,
    fulfillmentChangeReasonMessage,
    fulfillmentChangeAcknowledged,
  } = useWatch({
    control: form.control,
  });

  const { errors: fulfillmentChangeErrors } = useFormState({
    control: form.control,
    name: [
      "fulfillmentChangeAcknowledged",
      "fulfillmentChangeReason",
      "fulfillmentChangeReasonMessage",
    ],
  });

  const isOrderCardPaymentWithDeposit =
    order.paymentType === "cardPaymentWithDeposit";
  const hasBuyerDeclaredDeliveryDate = !!order.UnifiedCheckoutBasket;

  const canOrderPaymentDetailsChange =
    !order.paidAt &&
    !order.invoiceIssuedAt &&
    order.paymentType !== "checkout" &&
    order.paymentType !== "cardPaymentWithDeposit" &&
    order.price > 0 &&
    order.paymentType !== "sellarCredit";

  const { business } = useSellarCurrentBusiness();
  const supplierQuery = useSupplier(order.SupplierId, {
    enabled: !!order.SupplierId,
  });
  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList({
    query: [],
    sortBy: "createdAt",
    sortDir: "desc",
  });
  const supplierRelationshipQuery = useSupplierRelationshipList({
    query: [
      {
        type: "buyerId",
        modifier: "EQUALS",
        value: order.RetailerId,
      },
    ],
  });

  const buyerPostcode = order.Retailer.Business.DeliveryAddress.postalCode;

  const hiddenDeliveryOptionsWhenFinalised = (): Array<
    Extract<
      AllFulfillmentTypesType,
      "ups" | "apc" | "sellarDistribution" | "collection"
    >
  > => {
    if (!order.confirmedAt) return [];
    if (order.fulfillmentType === "sellarDistribution") {
      return ["collection"];
    }
    if (order.fulfillmentType === "collection") {
      return ["sellarDistribution"];
    }
    return [];
  };

  const {
    isFetched: deliveryAreaIsFetched,
    deliveryAreas,
    deliveryOptions,
    isReady: deliveryOptionsAreReady,
    isError: deliveryOptionsHaveError,
  } = useDeliveryOptions({
    postcode: buyerPostcode,
    hiddenOptions: [
      ...hiddenDeliveryOptionsWhenFinalised(),
      ...(modifyModal ? [] : (["ups", "apc", "dx", "dxDropOff"] as const)),
    ],
    forceDeliveryAreaFetch: true,
    includeShadowRoutes: business?.type === "supplier",
  });

  const fulfillmentOptions = deliveryOptions.map(({ name, value }) => ({
    name: value === "courier" ? "Courier" : name,
    value: value === "courier" && !modifyModal ? "sellar-shipping" : value,
  }));
  const getFulfillmentOptionValue = (value: OrderFulfillmentType) =>
    modifyModal ? value : getConfirmationFulfillmentOptionValue(value);
  const deliveryOptionsStatusMessage = deliveryOptionsHaveError
    ? "Could not load delivery options. Close and reopen this dialog to try again."
    : "Loading delivery options…";

  const directDeliveryArea = deliveryAreas?.find(
    (area) => !area.systemDefinedType,
  );

  const sdDeliveryArea = deliveryAreas?.find(
    (area) => area.systemDefinedType === "sellarDistribution",
  );

  const latestAvailablePreOrderFromDate = useMemo(
    () =>
      findLatestPreOrderAvailableFromDate(
        order.OrderItems.map((item) => ({
          availableFrom: item.Product.availableFrom,
        })),
      ),
    [order.OrderItems],
  );

  const paymentTerms =
    supplierRelationshipQuery.data?.pages[0]?.[0]?.paymentTerms;

  const supplierId = order.SupplierId;
  const productIds = order.OrderItems.map((item) => item.ProductId);

  const { nextDeliveryDate: sdDeliveryDate, nextDispatchDate: sdDispatchDate } =
    useDistributionDeliveryEstimate({
      supplierId,
      productIds,
      buyerPostcode,
      cutoffPolicy: "supplierOrderFinalisation",
    });

  useEffect(() => {
    const connection = supplierRelationshipQuery.data?.pages[0]?.[0];
    if (
      connection?.paymentDueCalculateFrom &&
      supplierQuery.data?.invoiceCreationStage === "confirmed" &&
      !modifyModal
    ) {
      form.setValue(
        "paymentDue",
        dayjs(
          getPaymentDue({
            paymentDueCalculateFrom: connection.paymentDueCalculateFrom,
            paymentDue: connection.paymentDue,
          }),
        )
          .endOf("day")
          .format("YYYY-MM-DD"),
      );
    }
  }, [supplierRelationshipQuery.data, form, modifyModal, supplierQuery.data]);

  useEffect(() => {
    if (paymentTerms === "paymentOnDelivery" && deliveryPlannedAt) {
      form.setValue(
        "paymentDue",
        dayjs(deliveryPlannedAt).format("YYYY-MM-DD"),
      );
    }
  }, [deliveryPlannedAt, form, paymentTerms]);

  if (courierDeliveryScheduleQuery.isSuccess) {
    const courierDeliverySchedule =
      courierDeliveryScheduleQuery.data.pages[0]?.[0];

    const deliveryPlannedAtInPast = deliveryPlannedAt
      ? dayjs().startOf("day").isAfter(dayjs(deliveryPlannedAt))
      : false;
    const shouldChangeDeliveryPlannedAt =
      (deliveryPlannedAt === null || deliveryPlannedAtInPast) &&
      order.paymentType !== "cardPaymentWithDeposit";

    const dispatchPlannedAtInPast = dispatchPlannedAt
      ? dayjs().startOf("day").isAfter(dayjs(dispatchPlannedAt))
      : false;
    const shouldChangeDispatchPlannedAt =
      (dispatchPlannedAt === null || dispatchPlannedAtInPast) &&
      order.paymentType !== "cardPaymentWithDeposit";

    if (
      fulfillmentType === "courier" ||
      fulfillmentType === "ups" ||
      fulfillmentType === ("sellar-shipping" as OrderFulfillmentType)
    ) {
      if (courierDeliverySchedule) {
        const { estimatedDispatchDate, estimatedDeliveryDate } =
          getCourierDeliveryAndDispatchDates({
            orderCutOffTime: dayjs(
              courierDeliverySchedule.orderCutOffTime,
            ).format("HH:mm"),
            dispatchDaysAfterConfirmation:
              courierDeliverySchedule.dispatchDaysAfterConfirmation,
            deliveryDaysAfterDispatch:
              courierDeliverySchedule.deliveryDaysAfterDispatch,
          });
        if (shouldChangeDeliveryPlannedAt) {
          form.setValue(
            "deliveryPlannedAt",
            estimatedDeliveryDate.format("YYYY-MM-DD"),
          );
        }
        if (shouldChangeDispatchPlannedAt) {
          form.setValue(
            "dispatchPlannedAt",
            estimatedDispatchDate.format("YYYY-MM-DD"),
          );
        }
      } else {
        if (shouldChangeDeliveryPlannedAt)
          form.setValue("deliveryPlannedAt", dayjs().format("YYYY-MM-DD"));
        if (shouldChangeDispatchPlannedAt)
          form.setValue("dispatchPlannedAt", dayjs().format("YYYY-MM-DD"));
      }
    }
  }

  useEffect(() => {
    if (!deliveryPlannedAt && business && deliveryAreaIsFetched) {
      if (fulfillmentType === "direct") {
        if (directDeliveryArea) {
          if (directDeliveryArea.DeliveryAreaDays) {
            const nextDeliveryDates = getNextDirectDeliveryDate({
              deliveryArea: directDeliveryArea,
              startDateReference: latestAvailablePreOrderFromDate ?? new Date(),
            });
            form.setValue(
              "deliveryPlannedAt",
              dayjs(nextDeliveryDates?.deliveryDate ?? new Date()).format(
                "YYYY-MM-DD",
              ),
            );
          } else {
            form.setValue("deliveryPlannedAt", dayjs().format("YYYY-MM-DD"));
          }
        } else {
          form.setValue("deliveryPlannedAt", dayjs().format("YYYY-MM-DD"));
        }
      } else if (
        fulfillmentType === "haulier" ||
        fulfillmentType === "collection"
      ) {
        form.setValue("deliveryPlannedAt", dayjs().format("YYYY-MM-DD"));
      } else if (fulfillmentType === "sellarDistribution") {
        if (dispatchPlannedAt) {
          form.setValue(
            "dispatchPlannedAt",
            dayjs(dispatchPlannedAt).format("YYYY-MM-DD"),
          );
        }
        form.setValue(
          "deliveryPlannedAt",
          dayjs(deliveryPlannedAt).format("YYYY-MM-DD"),
        );
      } else {
        form.setValue("deliveryPlannedAt", dayjs().format("YYYY-MM-DD"));
      }
    }
    if (!dispatchPlannedAt && fulfillmentType === "haulier") {
      form.setValue("dispatchPlannedAt", dayjs().format("YYYY-MM-DD"));
    }
  }, [
    business,
    deliveryAreaIsFetched,
    deliveryAreas,
    deliveryPlannedAt,
    directDeliveryArea,
    dispatchPlannedAt,
    form,
    fulfillmentType,
    latestAvailablePreOrderFromDate,
  ]);

  useEffect(() => {
    if (fulfillmentType === "sellarDistribution" && sdDeliveryArea) {
      const shouldRecalculateBuyerDeclaredDate =
        hasBuyerDeclaredDeliveryDate &&
        !!sdDeliveryDate &&
        !!order.deliveryPlannedAt &&
        dayjs(sdDeliveryDate).isAfter(order.deliveryPlannedAt, "day");

      if (hasBuyerDeclaredDeliveryDate && !shouldRecalculateBuyerDeclaredDate) {
        return;
      }

      if (sdDispatchDate) {
        form.setValue(
          "dispatchPlannedAt",
          dayjs(sdDispatchDate).format("YYYY-MM-DD"),
        );
      }

      if (sdDeliveryDate) {
        form.setValue(
          "deliveryPlannedAt",
          dayjs(sdDeliveryDate).format("YYYY-MM-DD"),
        );
      }
    }
  }, [
    form,
    fulfillmentType,
    hasBuyerDeclaredDeliveryDate,
    order.deliveryPlannedAt,
    sdDeliveryArea,
    sdDeliveryDate,
    sdDispatchDate,
  ]);

  useEffect(() => {
    if (order.price === 0) form.setValue("paidAt", dayjs().toDate());
  }, [form, order.price]);

  const orderPriceDecreased = order.paidAmount > order.price;
  const refundAmount = order.paidAmount - order.price;
  const showOrderTotalDecreased =
    order.paymentType === "checkout" && orderPriceDecreased;

  const isDistribution = fulfillmentType === "sellarDistribution";

  return (
    <>
      <InputLabel>Fulfillment type</InputLabel>
      <Controller
        control={form.control}
        name="fulfillmentType"
        disabled={modifyModal && (!!order.dispatchedAt || !!order.deliveredAt)}
        rules={{
          required: true,
          validate: (value) =>
            !deliveryOptionsAreReady
              ? deliveryOptionsStatusMessage
              : fulfillmentOptions.some(
                  (option) => option.value === getFulfillmentOptionValue(value),
                ) || "Choose an available fulfillment type.",
        }}
        render={({ field, fieldState }) => {
          const selectedOptionValue = getFulfillmentOptionValue(field.value);
          const hasAvailableSelection =
            deliveryOptionsAreReady &&
            fulfillmentOptions.some(
              (option) => option.value === selectedOptionValue,
            );

          return (
            <>
              <InputContainer>
                <InputSelect
                  {...field}
                  required
                  value={hasAvailableSelection ? selectedOptionValue : ""}
                  onChange={(event) => {
                    field.onChange(event);
                    form.setValue("fulfillmentChangeReason", undefined);
                    form.setValue("fulfillmentChangeReasonMessage", "");
                    form.setValue("fulfillmentChangeAcknowledged", false);
                  }}
                >
                  <InputOption value="" disabled hidden>
                    {deliveryOptionsAreReady
                      ? "Choose an available fulfillment type"
                      : deliveryOptionsStatusMessage}
                  </InputOption>
                  {deliveryOptionsAreReady &&
                    fulfillmentOptions.map(({ name, value }) => (
                      <InputOption key={value} value={value}>
                        {name}
                      </InputOption>
                    ))}
                </InputSelect>
              </InputContainer>
              {deliveryOptionsHaveError ? (
                <Alert type="error" message={deliveryOptionsStatusMessage} />
              ) : (
                (fieldState.error ||
                  (deliveryOptionsAreReady && !hasAvailableSelection)) && (
                  <InputNote>
                    {fieldState.error?.message ??
                      "The current fulfillment type is unavailable. Choose an available fulfillment type to continue."}
                  </InputNote>
                )
              )}
            </>
          );
        }}
      />

      <SellarDistributionFulfillmentChangeFormFragment
        currentFulfillmentType={order.fulfillmentType}
        newFulfillmentType={fulfillmentType ?? order.fulfillmentType}
        initiator={order.initiator}
        onChange={(reason) => {
          form.setValue("fulfillmentChangeReason", reason.reason);
          form.setValue("fulfillmentChangeReasonMessage", reason.message);
          form.setValue("fulfillmentChangeAcknowledged", reason.acknowledged);
          form.clearErrors("fulfillmentChangeReason");
          form.clearErrors("fulfillmentChangeReasonMessage");
          form.clearErrors("fulfillmentChangeAcknowledged");
        }}
        fulfillmentChangeReason={{
          reason: fulfillmentChangeReason,
          message: fulfillmentChangeReasonMessage ?? "",
          acknowledged: !!fulfillmentChangeAcknowledged,
        }}
        errors={fulfillmentChangeErrors}
        buyerName={order.Retailer.Business.name}
      />

      {/* UPS Weight Validation */}
      {fulfillmentType === "ups" &&
        (() => {
          const upsWeightValidation = validateUpsWeight(
            order.OrderItems || [],
            UPS_WEIGHT_CHANGE_DATE,
            dispatchPlannedAt ? new Date(dispatchPlannedAt) : currentDate,
          );

          if (!upsWeightValidation.isValid) {
            const exceededItemsText = upsWeightValidation.exceededItems
              .map((item) => `${item.productName} (${item.weight}kg)`)
              .join(", ");

            return (
              <Alert
                className={"w-full"}
                type={"error"}
                message={` The following items exceed the UPS weight limit: ${exceededItemsText}. Please select a different fulfillment type or remove these items.`}
                title={`UPS cannot ship items over ${upsWeightValidation.maxAllowedWeight}kg`}
              />
            );
          }
          return null;
        })()}

      {fulfillmentType === ("sellar-shipping" as OrderFulfillmentType) &&
        !modifyModal && (
          <>
            <div className={"mt-1 mb-1"}>
              <InputNote>
                You'll be able to choose your service provider on the next
                screen
              </InputNote>
            </div>
            {order.deliveryChargeCapped && (
              <Alert
                className={"w-full"}
                type={"information"}
                message={""}
                title={
                  "This order has reached your courier delivery charge cap"
                }
              />
            )}
          </>
        )}

      {/* NOTE: For cardPaymentWithDeposit payment type, estimated dispatch/delivery dates are set via order update dialog manually */}
      {(!isOrderCardPaymentWithDeposit || modifyModal || isDistribution) && (
        <>
          <InputLabel>
            {renderDeliveryDateTitle(fulfillmentType)} date
          </InputLabel>

          {courierDeliveryScheduleQuery.isFetched && (
            <InputContainer
              className={twMerge(
                "flex gap-2",
                Boolean(isDistribution && !isAdmin) &&
                  "pointer-events-none text-zinc-500",
              )}
            >
              {isDistribution && <Icon icon="LOCK" />}
              <InputDate
                {...form.register("deliveryPlannedAt", {
                  required: true,
                  valueAsDate: true,
                  disabled: !!deliveredAt,
                  value: deliveryPlannedAt,
                })}
                readOnly={hasBuyerDeclaredDeliveryDate}
                min={dayjs().startOf("day").format("YYYY-MM-DD")}
              />
            </InputContainer>
          )}
          {(isDistribution || hasBuyerDeclaredDeliveryDate) && (
            <p className="text-zinc-500">
              {hasBuyerDeclaredDeliveryDate
                ? "This delivery date was chosen by the buyer and cannot be changed"
                : "This date is set by the Sellar Distribution team and cannot be changed"}
            </p>
          )}
          {[
            ORDER_FULFILLMENT_TYPES.HAULIER.value,
            ORDER_FULFILLMENT_TYPES.COURIER.value,
            ORDER_FULFILLMENT_TYPES.UPS.value,
            ORDER_FULFILLMENT_TYPES.DX_BASIC.value,
            ORDER_FULFILLMENT_TYPES.DISTRIBUTION.value,
            "sellar-shipping",
          ].includes(
            (modifyModal || isDistribution) && order.confirmedAt
              ? order.fulfillmentType
              : (fulfillmentType ?? order.fulfillmentType),
          ) &&
            courierDeliveryScheduleQuery.isFetched && (
              <>
                <InputLabel>
                  Estimated{" "}
                  {isDistribution ? "collection date" : "dispatch date"}
                </InputLabel>
                <InputContainer
                  className={twMerge(
                    "flex gap-2",
                    Boolean(isDistribution && !isAdmin) &&
                      "pointer-events-none text-zinc-500",
                  )}
                >
                  {isDistribution && <Icon icon="LOCK" />}
                  <InputDate
                    {...form.register("dispatchPlannedAt", {
                      required: true,
                      /* Note: This can cause issues where if dispatchPlannedAt is not conditionally rendered this is still happening before validation
          and as the value will be null/undefined this results in an InvalidDate, see react hook form docs:
          https://www.react-hook-form.com/api/useform/register/#:~:text=%7D%0A/%3E-,valueAsDate,-%3A%0Aboolean
          */
                      valueAsDate: true,
                      disabled: !!dispatchedAt,
                      value: dispatchPlannedAt,
                    })}
                    min={dayjs().startOf("day").format("YYYY-MM-DD")}
                  />
                </InputContainer>
                {isDistribution && (
                  <p className="text-zinc-500">
                    We will inform you of any change to your collection date
                  </p>
                )}
              </>
            )}
        </>
      )}
      {!order.stripeInvoiceId && (
        <>
          {canOrderPaymentDetailsChange && (
            <OrderPaymentMethodFormFragment
              disabled={!!order.paymentDue}
              buyerName={order.Retailer.Business.name}
              paymentType={paymentType}
              retailerPreference={order.paymentType}
              stripeAccountId={order.Supplier.stripeAccountId}
              onChange={(e) =>
                form.setValue(
                  "paymentType",
                  e.currentTarget.value as PaymentPreferenceType,
                )
              }
            />
          )}
          {canOrderPaymentDetailsChange && (
            <>
              {(supplierQuery.data?.invoiceCreationStage === "confirmed" ||
                paymentTerms === "paymentOnDelivery") &&
              order.price > 0 &&
              !modifyModal ? (
                <>
                  <InputLabel>Payment due date</InputLabel>
                  <InputContainer>
                    <InputDate
                      min={dayjs()
                        .add(
                          paymentTerms === "paymentOnDelivery" ? 0 : 1,
                          "day",
                        )
                        .startOf("day")
                        .format("YYYY-MM-DD")}
                      {...form.register("paymentDue", {
                        required: true,
                        valueAsDate: true,
                      })}
                    />
                  </InputContainer>
                </>
              ) : (
                <div className={"my-4"}>
                  <InputNote>
                    <strong>
                      The invoice for this order will be issued on order{" "}
                      {supplierQuery.data?.invoiceCreationStage}
                    </strong>{" "}
                    as defined by your invoice settings. You'll be able to
                    review and confirm the payment due date upon marking as{" "}
                    {supplierQuery.data?.invoiceCreationStage}.
                  </InputNote>
                </div>
              )}
            </>
          )}
          <InputLabel>Purchase order number</InputLabel>
          <InputContainer>
            <InputText
              {...form.register("purchaseOrderNumber", {
                required: false,
                disabled: !canEditInvoice(order),
              })}
            />
          </InputContainer>
          <div className={"flex items-center gap-2"}>
            <Switch
              checked={!!hasNoDraughtDutyRelief}
              onChange={() => {
                form.setValue(
                  "hasNoDraughtDutyRelief",
                  !hasNoDraughtDutyRelief,
                );
              }}
              className={"m-2"}
              disabled={!!order.confirmedAt || !!order.invoiceIssuedAt}
            />
            <p>Full duty paid on draught items required</p>
          </div>
          {paymentType === ORDER_PAYMENT_TYPES.MANUAL.value &&
            order.price > 0 && (
              <InputField label={"Has this order been paid?"}>
                <InputSelect
                  disabled={!!order.paidAt}
                  required={true}
                  value={!!paidAt ? "yes" : "no"}
                  onChange={(e) => {
                    if (e.target.value === "yes") {
                      form.setValue("paidAt", dayjs().toDate());
                    } else {
                      form.setValue("paidAt", null);
                    }
                  }}
                >
                  <InputOption value="" disabled hidden>
                    Choose...
                  </InputOption>
                  <option value={"yes"}>Yes</option>
                  <option value={"no"}>No</option>
                </InputSelect>
              </InputField>
            )}
        </>
      )}
      {showOrderTotalDecreased && (
        <Alert
          message={""}
          title={"Order total decreased"}
          type={"information"}
          className={"w-full"}
        >
          <p>
            By confirming this order, you will{" "}
            <strong>automatically issue</strong> {order.Retailer.Business.name}{" "}
            a refund of {formatCurrency(refundAmount)} for the difference.
          </p>
        </Alert>
      )}
      {isDistribution && (
        <Alert
          message={""}
          title={"Sellar Distribution order"}
          type={"information"}
          className={"w-full"}
        >
          <p className="text-base">
            By finalising this order today, your customer will get an estimated
            delivery date of {dayjs(deliveryPlannedAt).format("DD/MM/YYYY")},
            and{" "}
            {sdDeliveryArea?.distributionWarehouseSupplierRelationship ===
            "palletInbound" ? (
              `you will need to dispatch the order to arrive at our warehouse for ${dayjs(dispatchPlannedAt).format("DD/MM/YYYY")}.`
            ) : (
              <>
                <strong>
                  we will aim to collect from you on{" "}
                  {dayjs(dispatchPlannedAt).format("DD/MM/YYYY")}
                </strong>
                . We will update you if there is any change to these estimated
                dates.
              </>
            )}
          </p>
        </Alert>
      )}
    </>
  );
}
